import React from "react"
import styled, { css } from "styled-components"
import { up } from "styled-breakpoints"
import colors from "@constants/colors"
import breakpoints from "@constants/breakpoints"
import { DESKTOP_HERO_HEIGHT } from "@constants/measurements"
import { h6 } from "@constants/typography"
import { ChevronLarge } from "@components/icons"
import HlsVideo from "@components/HlsVideo"
import { canPlayBannerVideo } from "./playback"

const NO_BANNER_HEIGHT_REDUCTION = 250

export interface StripVideoBannerItem {
  title: string
  desc1?: string
  desc2?: string
  pill?: string
  logo: string
  video: string
  streamingUrl?: string
  fallbackVideoUrl?: string
  overlayRgb: string
  posterSrc?: string
  redirect?: string
}

interface StripVideoBannerProps {
  items: StripVideoBannerItem[]
  height?: string
  mobileHeight?: string
  promoBannerImage?: {
    src: string
    alt?: string
  }
  promoBannerMobileImage?: {
    src: string
    alt?: string
  }
  promoBannerImages?: Array<{
    src: string
    alt?: string
  }>
  promoBannerMobileImages?: Array<{
    src: string
    alt?: string
  }>
}

const Wrapper = styled.section<{ height: string }>`
  position: relative;
  display: none;
  width: 100%;
  height: ${({ height }) => height};
  overflow: hidden;

  ${up("laptop")} {
    display: flex;
  }
`

const MobileWrapper = styled.section<{ height: string }>`
  position: relative;
  display: flex;
  width: 100%;
  height: ${({ height }) => height};
  overflow: hidden;

  ${up("laptop")} {
    display: none;
  }
`

const Section = styled.section<{ height: string; mobileHeight: string }>`
  position: relative;
  width: 100%;
  height: ${({ mobileHeight }) => mobileHeight};
  overflow: hidden;

  ${up("laptop")} {
    height: ${({ height }) => height};
  }
`

const Item = styled.button<{ isHovered: boolean }>`
  position: relative;
  flex: 1 1 0;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  overflow: hidden;
  transition: flex 280ms ease;

  ${({ isHovered }) =>
    isHovered &&
    css`
      flex: 3.4 1 0;
    `}

  &:focus-visible {
    outline: 2px solid ${colors.snow};
    outline-offset: -2px;
  }
`

const Video = styled(HlsVideo)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: grayscale(10%);
  transform: scale(1);
  transition: transform 380ms ease;

  ${Item}:hover & {
    transform: scale(1.03);
  }
`

const MobileSlide = styled.div<{ isActive: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  pointer-events: ${({ isActive }) => (isActive ? "auto" : "none")};
  transition: opacity 220ms ease;
`

const MobileNavButton = styled.button<{ side: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${({ side }) => (side === "left" ? "left: 16px;" : "right: 16px;")}
  transform: translateY(-50%);
  z-index: 2;
  width: 36px;
  height: 36px;
  padding: 6px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(0, 0, 0, 0.35);
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.5);
  }

  &:focus-visible {
    outline: 2px solid ${colors.snow};
    outline-offset: 2px;
  }
`

const Overlay = styled.div<{ rgb: string; isHovered: boolean }>`
  position: absolute;
  inset: 0;
  background-color: ${({ rgb, isHovered }) =>
    `rgba(${rgb}, ${isHovered ? 0.05 : 0.65})`};
  transition: background-color 220ms ease;
`

const Content = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
  padding: 24px;
  color: ${colors.obsidian};
  align-items: center;
  justify-content: center;
  text-align: center;
`

const Logo = styled.img.attrs({
  decoding: "async",
})`
  height: 64px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
`

const Detail = styled.p`
  margin: 0;
  max-width: 360px;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.snow};
  opacity: 0.9;
`

const ctaStyles = css<{ isClickable: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 22px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  color: ${colors.snow};
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: ${({ isClickable }) => (isClickable ? "pointer" : "default")};
  transition: background-color 200ms ease, color 200ms ease,
    border-color 200ms ease;

  @media (hover: hover) {
    ${({ isClickable }) =>
      isClickable &&
      css`
        &:hover {
          background: ${colors.snow};
          color: ${colors.obsidian};
          border-color: ${colors.snow};
        }
      `}
  }
`

const Cta = styled.span<{ isClickable: boolean }>`
  ${ctaStyles}
`

const CtaButton = styled.span<{ isClickable: boolean }>`
  ${ctaStyles}
`

const Title = styled.h6`
  ${h6}
  font-size: 28px;
  line-height: 32px;
  color: ${colors.snow};
`

const PromoBannerWrap = styled.div`
  width: 100%;
  background: ${colors.silver};
  transition: box-shadow 250ms ease;
`

const DesktopPromoBannerWrap = styled(PromoBannerWrap)`
  display: none;

  ${up("mobileXL")} {
    display: block;
  }
`

const MobilePromoBannerWrap = styled(PromoBannerWrap)`
  display: block;

  ${up("mobileXL")} {
    display: none;
  }
`

const PromoBannerImage = styled.img.attrs({
  decoding: "async",
  loading: "lazy",
})`
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  transition: transform 250ms ease, filter 250ms ease;
`

const PromoSliderViewport = styled.div`
  width: 100%;
  overflow: hidden;
`

const PromoSliderTrack = styled.div<{ index: number; count: number }>`
  display: flex;
  width: ${({ count }) => `${count * 100}%`};
  transform: ${({ index, count }) =>
    `translateX(-${(index * 100) / Math.max(count, 1)}%)`};
  transition: transform 420ms ease;
`

const PromoSlide = styled.div<{ count: number }>`
  width: ${({ count }) => `${100 / Math.max(count, 1)}%`};
  flex-shrink: 0;
`

const PromoDots = styled.div`
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 2;
`

const PromoDot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border: 0;
  border-radius: 999px;
  padding: 0;
  cursor: pointer;
  background: ${({ active }) =>
    active ? colors.snow : "rgba(255, 255, 255, 0.45)"};
`

const StripVideoBanner: React.FC<StripVideoBannerProps> = ({
  items,
  height = DESKTOP_HERO_HEIGHT,
  mobileHeight,
  promoBannerImage,
  promoBannerMobileImage,
  promoBannerImages,
  promoBannerMobileImages,
}) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null)
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const [promoIndex, setPromoIndex] = React.useState(0)
  const [isDesktopViewport, setIsDesktopViewport] = React.useState(false)
  const [isDocumentVisible, setIsDocumentVisible] = React.useState(true)
  const [isSectionVisible, setIsSectionVisible] = React.useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false)
  const [saveDataEnabled, setSaveDataEnabled] = React.useState(false)
  const [endedMobileIndices, setEndedMobileIndices] = React.useState<number[]>(
    []
  )
  const desktopPromoSlides = React.useMemo(() => {
    const list = promoBannerImages?.filter((item) => Boolean(item?.src)) || []
    if (list.length > 0) return list
    return promoBannerImage?.src ? [promoBannerImage] : []
  }, [promoBannerImage, promoBannerImages])

  const mobilePromoSlides = React.useMemo(() => {
    const list =
      promoBannerMobileImages?.filter((item) => Boolean(item?.src)) || []
    if (list.length > 0) return list
    if (promoBannerMobileImage?.src) return [promoBannerMobileImage]
    return desktopPromoSlides
  }, [desktopPromoSlides, promoBannerMobileImage, promoBannerMobileImages])

  const promoSlideCount = Math.max(
    desktopPromoSlides.length,
    mobilePromoSlides.length
  )
  const hasPromoBanner = Boolean(
    desktopPromoSlides.length || mobilePromoSlides.length
  )
  const sectionRef = React.useRef<HTMLElement | null>(null)
  const desktopPromoBannerRef = React.useRef<HTMLDivElement | null>(null)
  const mobilePromoBannerRef = React.useRef<HTMLDivElement | null>(null)
  const [videoAreaHeight, setVideoAreaHeight] = React.useState<number | null>(
    null
  )
  const fallbackVideoHeight = `calc(${height} - ${NO_BANNER_HEIGHT_REDUCTION}px)`
  const fallbackMobileVideoHeight = `calc(${
    mobileHeight || height
  } - ${NO_BANNER_HEIGHT_REDUCTION}px)`
  const resolvedSectionHeight = hasPromoBanner ? height : fallbackVideoHeight
  const resolvedMobileSectionHeight = hasPromoBanner
    ? mobileHeight || height
    : fallbackMobileVideoHeight
  const isPlaybackAllowed = canPlayBannerVideo({
    isDocumentVisible,
    isSectionVisible,
    prefersReducedMotion,
    saveDataEnabled,
  })

  React.useEffect(() => {
    setPromoIndex(0)
  }, [promoSlideCount])

  React.useEffect(() => {
    if (typeof window === "undefined") return

    const mediaQuery = window.matchMedia(`(min-width: ${breakpoints.laptop}px)`)
    const updateViewport = () => {
      setIsDesktopViewport(mediaQuery.matches)
    }

    updateViewport()
    mediaQuery.addEventListener("change", updateViewport)

    return () => {
      mediaQuery.removeEventListener("change", updateViewport)
    }
  }, [])

  React.useEffect(() => {
    if (promoSlideCount <= 1 || !isPlaybackAllowed) return
    const timer = window.setInterval(() => {
      setPromoIndex((old) => (old + 1) % promoSlideCount)
    }, 5000)
    return () => window.clearInterval(timer)
  }, [isPlaybackAllowed, promoSlideCount])

  React.useEffect(() => {
    if (typeof window === "undefined") return

    const sectionNode = sectionRef.current
    if (!sectionNode || typeof IntersectionObserver === "undefined") {
      setIsSectionVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsSectionVisible(entry.isIntersecting),
      { threshold: 0.05 }
    )

    observer.observe(sectionNode)
    return () => observer.disconnect()
  }, [])

  React.useEffect(() => {
    if (typeof window === "undefined") return

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
    const updateMotionPreference = () => {
      setPrefersReducedMotion(motionQuery.matches)
    }
    const updateDocumentVisibility = () => {
      setIsDocumentVisible(document.visibilityState !== "hidden")
    }
    const connection = (
      navigator as Navigator & { connection?: { saveData?: boolean } }
    ).connection

    updateMotionPreference()
    updateDocumentVisibility()
    setSaveDataEnabled(Boolean(connection?.saveData))
    document.addEventListener("visibilitychange", updateDocumentVisibility)

    if (motionQuery.addEventListener) {
      motionQuery.addEventListener("change", updateMotionPreference)
    } else {
      motionQuery.addListener(updateMotionPreference)
    }

    return () => {
      document.removeEventListener("visibilitychange", updateDocumentVisibility)
      if (motionQuery.removeEventListener) {
        motionQuery.removeEventListener("change", updateMotionPreference)
      } else {
        motionQuery.removeListener(updateMotionPreference)
      }
    }
  }, [])

  React.useEffect(() => {
    if (!hasPromoBanner) {
      setVideoAreaHeight(null)
      return
    }

    const sectionNode = sectionRef.current
    if (!sectionNode) return

    const updateHeights = () => {
      const sectionHeight = sectionNode.getBoundingClientRect().height
      const desktopPromoHeight =
        desktopPromoBannerRef.current?.getBoundingClientRect().height || 0
      const mobilePromoHeight =
        mobilePromoBannerRef.current?.getBoundingClientRect().height || 0
      const promoHeight = Math.max(desktopPromoHeight, mobilePromoHeight)
      setVideoAreaHeight(Math.max(sectionHeight - promoHeight, 0))
    }

    updateHeights()

    const resizeObserver = new ResizeObserver(updateHeights)
    resizeObserver.observe(sectionNode)
    if (desktopPromoBannerRef.current) {
      resizeObserver.observe(desktopPromoBannerRef.current)
    }
    if (mobilePromoBannerRef.current) {
      resizeObserver.observe(mobilePromoBannerRef.current)
    }
    window.addEventListener("resize", updateHeights)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("resize", updateHeights)
    }
  }, [hasPromoBanner, promoSlideCount])

  React.useEffect(() => {
    if (items.length <= 1 || !isPlaybackAllowed) return
    const timer = window.setInterval(() => {
      setCurrentIndex((old) => (old + 1) % items.length)
    }, 5000)
    return () => window.clearInterval(timer)
  }, [isPlaybackAllowed, items.length])

  const goNext = React.useCallback(() => {
    setCurrentIndex((old) => (old + 1) % items.length)
  }, [items.length])

  const goPrev = React.useCallback(() => {
    setCurrentIndex((old) => (old - 1 + items.length) % items.length)
  }, [items.length])

  const handleEnter = React.useCallback((i: number) => {
    setHoveredIndex(i)
  }, [])

  const handleLeave = React.useCallback((i: number) => {
    setHoveredIndex((current) => (current === i ? null : current))
  }, [])

  const redirectTo = React.useCallback((url?: string) => {
    if (!url) return
    window.location.assign(url)
  }, [])

  const handleDesktopItemClick = React.useCallback(
    (redirect?: string) => {
      redirectTo(redirect)
    },
    [redirectTo]
  )

  const shouldLoadDesktopVideo = React.useCallback(
    (index: number) =>
      isPlaybackAllowed && isDesktopViewport && hoveredIndex === index,
    [hoveredIndex, isDesktopViewport, isPlaybackAllowed]
  )

  const shouldLoadMobileVideo = React.useCallback(
    (index: number) =>
      isPlaybackAllowed && !isDesktopViewport && currentIndex === index,
    [currentIndex, isDesktopViewport, isPlaybackAllowed]
  )

  return (
    <Section
      ref={sectionRef}
      height={resolvedSectionHeight}
      mobileHeight={resolvedMobileSectionHeight}
    >
      <Wrapper
        height={
          videoAreaHeight !== null
            ? `${videoAreaHeight}px`
            : fallbackVideoHeight
        }
      >
        {items.map((item, i) => (
          <Item
            key={item.title}
            isHovered={hoveredIndex === i}
            onMouseEnter={() => handleEnter(i)}
            onMouseLeave={() => handleLeave(i)}
            onFocus={() => handleEnter(i)}
            onBlur={() => handleLeave(i)}
            onClick={() => handleDesktopItemClick(item.redirect)}
            type="button"
          >
            <Video
              muted
              maxPlayCount={3}
              playsInline
              preload="none"
              poster={item.posterSrc}
              streamingUrl={item.streamingUrl}
              fallbackUrl={item.fallbackVideoUrl || item.video}
              active={shouldLoadDesktopVideo(i)}
              aria-hidden="true"
            />
            <Overlay rgb={item.overlayRgb} isHovered={hoveredIndex === i} />
            <Content>
              <Logo src={item.logo} alt={item.title} />
              <Title>{item.title}</Title>
              {(item.desc1 || item.desc2) && (
                <Detail>
                  {item.desc1}
                  {item.desc2 && (
                    <>
                      <br />
                      {item.desc2}
                    </>
                  )}
                </Detail>
              )}
              {item.pill && (
                <Cta data-cta="true" isClickable={Boolean(item.redirect)}>
                  {item.pill}
                </Cta>
              )}
            </Content>
          </Item>
        ))}
      </Wrapper>
      <MobileWrapper
        height={
          videoAreaHeight !== null
            ? `${videoAreaHeight}px`
            : fallbackMobileVideoHeight
        }
      >
        {items.length > 1 && (
          <>
            <MobileNavButton side="left" onClick={goPrev} aria-label="Previous">
              <ChevronLarge
                style={{ transform: "rotate(180deg)" }}
                strokeColor={colors.snow}
                strokeWidth={2}
              />
            </MobileNavButton>
            <MobileNavButton side="right" onClick={goNext} aria-label="Next">
              <ChevronLarge strokeColor={colors.snow} strokeWidth={2} />
            </MobileNavButton>
          </>
        )}
        {items.map((item, i) => (
          <MobileSlide
            key={item.title}
            isActive={currentIndex === i}
            onClick={() => redirectTo(item.redirect)}
          >
            <Video
              muted
              playsInline
              preload="none"
              poster={item.posterSrc}
              streamingUrl={item.streamingUrl}
              fallbackUrl={item.fallbackVideoUrl || item.video}
              preservePlaybackState
              active={
                shouldLoadMobileVideo(i) && !endedMobileIndices.includes(i)
              }
              aria-hidden="true"
              onEnded={() => {
                setEndedMobileIndices((current) =>
                  current.includes(i) ? current : [...current, i]
                )
              }}
            />
            <Overlay rgb={item.overlayRgb} isHovered={false} />
            <Content>
              <Logo src={item.logo} alt={item.title} />
              <Title>{item.title}</Title>
              {(item.desc1 || item.desc2) && (
                <Detail>
                  {item.desc1}
                  {item.desc2 && (
                    <>
                      <br />
                      {item.desc2}
                    </>
                  )}
                </Detail>
              )}
              {item.pill && (
                <CtaButton isClickable={Boolean(item.redirect)} data-cta="true">
                  {item.pill}
                </CtaButton>
              )}
            </Content>
          </MobileSlide>
        ))}
      </MobileWrapper>
      {hasPromoBanner && (
        <>
          <DesktopPromoBannerWrap>
            <PromoSliderViewport ref={desktopPromoBannerRef}>
              <PromoSliderTrack
                index={promoIndex % desktopPromoSlides.length}
                count={desktopPromoSlides.length}
              >
                {desktopPromoSlides.map((slide, index) => (
                  <PromoSlide
                    key={`desktop-promo-slide-${index}`}
                    count={desktopPromoSlides.length}
                  >
                    <PromoBannerImage
                      src={slide.src}
                      alt={slide.alt || "Promo banner"}
                    />
                  </PromoSlide>
                ))}
              </PromoSliderTrack>
            </PromoSliderViewport>
            {promoSlideCount > 1 && (
              <PromoDots>
                {Array.from({ length: promoSlideCount }).map((_, index) => (
                  <PromoDot
                    key={`desktop-promo-dot-${index}`}
                    type="button"
                    active={index === promoIndex}
                    aria-label={`View promo banner ${index + 1}`}
                    onClick={() => setPromoIndex(index)}
                  />
                ))}
              </PromoDots>
            )}
          </DesktopPromoBannerWrap>
          <MobilePromoBannerWrap>
            <PromoSliderViewport ref={mobilePromoBannerRef}>
              <PromoSliderTrack
                index={promoIndex % mobilePromoSlides.length}
                count={mobilePromoSlides.length}
              >
                {mobilePromoSlides.map((slide, index) => (
                  <PromoSlide
                    key={`mobile-promo-slide-${index}`}
                    count={mobilePromoSlides.length}
                  >
                    <PromoBannerImage
                      src={slide.src}
                      alt={slide.alt || "Promo banner"}
                    />
                  </PromoSlide>
                ))}
              </PromoSliderTrack>
            </PromoSliderViewport>
            {promoSlideCount > 1 && (
              <PromoDots>
                {Array.from({ length: promoSlideCount }).map((_, index) => (
                  <PromoDot
                    key={`mobile-promo-dot-${index}`}
                    type="button"
                    active={index === promoIndex}
                    aria-label={`View promo banner ${index + 1}`}
                    onClick={() => setPromoIndex(index)}
                  />
                ))}
              </PromoDots>
            )}
          </MobilePromoBannerWrap>
        </>
      )}
    </Section>
  )
}

export default StripVideoBanner
